import React, { useEffect, useState } from 'react';
import {
  internsByMunicipality,
  internsByCategories,
  internsBySkills,
  internsByGender,
  internsByAge,
  internsByEthnicity,
  internsByHealthCondition,
} from '../../../store/actions/actions';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FileDownloadOutlined } from '@mui/icons-material';
import { Button, CircularProgress, Grid } from '@mui/material';
import { toastNotification } from '../../../utils/toastNotification';
import APIClient from '../../../services/ApiClient';
import { saveAs } from 'file-saver';
import { InternFilter } from './Filters/Intern';
import MainCard from '../../../utils/MainCard';
import SubCard from '../../../utils/SubCard';
import { StatsChart } from './Stats';

export const InternsReports = ({ startDate, endDate }) => {
  const internsByMunicipalityResponse = useSelector(({ reports }) => reports.internsByMunicipality);
  const internsBySkillsResponse = useSelector(({ reports }) => reports.internsBySkills);
  const internsByCategoriesResponse = useSelector(({ reports }) => reports.internsByCategories);
  const internsByGenderResponse = useSelector(({ reports }) => reports.internsByGender);
  const internsByAgeResponse = useSelector(({ reports }) => reports.internsByAge);
  // const internsByEthnicityResponse = useSelector(({ reports }) => reports.internsByEthnicity);
  const internsByHealthConditionResponse = useSelector(({ reports }) => reports.internsByHealthCondition);

  const [loadingButton, setLoadingButton] = useState(false);
  const [municipalityChips, setMunicipalityChips] = useState([]);
  const [genderChips, setGenderChips] = useState([]);
  // const [ethnicityChips, setEthnicityChips] = useState([]);
  const [healthConditionChips, setHealthConditionChips] = useState([]);
  const [skillChips, setSkillChips] = useState([]);
  const [categoryChips, setCategoryChips] = useState([]);
  const [selectedYear, setSelectedYear] = useState('choose');

  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (startDate || endDate) {
      let payload = { startDate: startDate ? startDate : undefined, endDate: endDate ? endDate : undefined };
      if (skillChips?.length) payload['skills'] = skillChips.map((skill) => skill._id);
      if (categoryChips?.length) payload['categories'] = categoryChips.map((cat) => cat._id);
      if (municipalityChips?.length) payload['municipalities'] = municipalityChips.map((mun) => mun._id);
      if (genderChips?.length) payload['genders'] = genderChips.map((e) => e._id);
      if (selectedYear !== 'choose') payload['birthYear'] = String(selectedYear);
      // if (ethnicityChips?.length) payload['ethnicities'] = ethnicityChips.map((e) => e._id);
      // if (healthConditionChips?.length) payload['healthConditions'] = healthConditionChips.map((e) => e._id);
      dispatch(internsByMunicipality(payload));
      dispatch(internsBySkills(payload));
      dispatch(internsByCategories(payload));
      dispatch(internsByGender(payload));
      dispatch(internsByAge(payload));
      // dispatch(internsByEthnicity(payload));
      dispatch(internsByHealthCondition(payload));
    }
  }, [genderChips, healthConditionChips, skillChips, categoryChips, selectedYear, municipalityChips]); //municipalityChips, ethnicityChips

  const onDocumentDownload = async (event) => {
    setLoadingButton(true);
    event.preventDefault();
    let payload = {};
    if (endDate) payload['endDate'] = endDate;
    if (startDate) payload['startDate'] = startDate;
    if (skillChips?.length) payload['skills'] = skillChips.map((skill) => skill._id);
    if (categoryChips?.length) payload['categories'] = categoryChips.map((cat) => cat._id);
    if (municipalityChips?.length) payload['municipalities'] = municipalityChips.map((mun) => mun._id);
    if (genderChips?.length) payload['genders'] = genderChips.map((e) => e._id);
    if (selectedYear !== 'choose') payload['birthYear'] = String(selectedYear);
    // if (ethnicityChips?.length) payload['ethnicities'] = ethnicityChips.map((e) => e._id);
    // if (healthConditionChips?.length) payload['healthConditions'] = healthConditionChips.map((e) => e._id);
    await APIClient.post(`/reports/interns/export`, payload, {
      responseType: 'blob',
    })
      .then((res) => {
        setLoadingButton(false);
        if (res.data) {
          if (res.headers['content-type'] == 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
            saveAs(res.data, 'Interns_Stats.csv');
          }
        } else {
          return toastNotification('error', 'Something went wrong!');
        }
      })
      .catch((err) => {
        setLoadingButton(false);
        return toastNotification('error', 'Something went wrong!');
      });
  };

  if (
    internsByMunicipalityResponse.loading ||
    internsBySkillsResponse.loading ||
    internsByCategoriesResponse.loading ||
    internsByGenderResponse.loading ||
    internsByAgeResponse.loading ||
    // internsByEthnicityResponse.loading ||
    internsByHealthConditionResponse.loading
  ) {
    return <div class="Stats-loader">Loading...</div>;
  }
  return (
    <>
      <MainCard title={t('generalStatistics')} sx={{ color: '#01486B', fontFamily: 'Mark-Bold !important' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <InternFilter
                  categoryChips={categoryChips}
                  skillChips={skillChips}
                  municipalityChips={municipalityChips}
                  genderChips={genderChips}
                  // ethnicityChips={ethnicityChips}
                  healthConditionChips={healthConditionChips}
                  setCategoryChips={setCategoryChips}
                  setSkillChips={setSkillChips}
                  setMunicipalityChips={setMunicipalityChips}
                  setGenderChips={setGenderChips}
                  // setEthnicityChips={setEthnicityChips}
                  setHealthConditionChips={setHealthConditionChips}
                  selectedYear={selectedYear}
                  setSelectedYear={setSelectedYear}
                />
              </Grid>
              <Grid item xs={12}>
                <SubCard
                  title={t('charts')}
                  sx={{ color: '#01486B', fontFamily: 'Mark-Bold !important' }}
                  secondary={
                    <Button
                      variant="contained"
                      disabled={loadingButton === true ? true : false}
                      sx={{
                        backgroundColor: '#01486B !important',
                        '&:hover': { backgroundColor: '#012b40 !important' },
                        width: '160px',
                        fontFamily: 'Mark-Regular',
                        fontSize: '14px',
                        textTransform: 'none !important',
                      }}
                      onClick={(e) => {
                        onDocumentDownload(e);
                      }}
                    >
                      {loadingButton ? (
                        <>
                          <CircularProgress size={24} style={{ color: '#fff' }} />
                        </>
                      ) : (
                        <>
                          <FileDownloadOutlined /> {t('exportExcel')}
                        </>
                      )}
                    </Button>
                  }
                >
                  <Grid container spacing={3}>
                    <StatsChart
                      response={internsByMunicipalityResponse}
                      label={t('internsByMunicipality')}
                      data={
                        internsByMunicipalityResponse?.data?.interns ? internsByMunicipalityResponse.data.interns : []
                      }
                      chart={'Bar'}
                    />
                    <StatsChart
                      response={internsBySkillsResponse}
                      label={t('internsBySkills')}
                      data={internsBySkillsResponse?.data?.interns ? internsBySkillsResponse.data.interns : []}
                      chart={'Bar'}
                    />
                    <StatsChart
                      response={internsByCategoriesResponse}
                      label={t('internsByCategories')}
                      data={internsByCategoriesResponse?.data?.interns ? internsByCategoriesResponse.data.interns : []}
                      chart={'Bar'}
                    />
                    <StatsChart
                      response={internsByGenderResponse}
                      label={t('internsByGender')}
                      data={internsByGenderResponse?.data?.interns ? internsByGenderResponse.data.interns : []}
                      chart={'Pie'}
                    />
                    <StatsChart
                      response={internsByAgeResponse}
                      label={t('internsByAge')}
                      data={internsByAgeResponse?.data?.interns ? internsByAgeResponse.data.interns : []}
                      chart={'Bar'}
                    />
                    {/* <StatsChart
                      response={internsByEthnicityResponse}
                      label={t('internsByEthnicity')}
                      data={internsByEthnicityResponse?.data?.interns ? internsByEthnicityResponse.data.interns : []}
                      chart={'Bar'}
                    /> */}
                    {/* <StatsChart
                      response={internsByHealthConditionResponse}
                      label={t('internsByHealthCondition')}
                      data={
                        internsByHealthConditionResponse?.data?.interns
                          ? internsByHealthConditionResponse.data.interns
                          : []
                      }
                      chart={'Bar'}
                    /> */}
                  </Grid>
                </SubCard>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainCard>
    </>
  );
};
